import { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { Form, Button, Spinner } from 'react-bootstrap';
import ReactMarkdown from 'react-markdown';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperclip } from '@fortawesome/free-solid-svg-icons';
import DocumentGrid from './DocumentGrid';
import FileSelector from './FileSelector';
import {
  fetchChatsByClaim,
  fetchChatMessages,
  useDeleteChat,
} from '../../services/casegpt';
import { getCookie } from '../../lib/cookies';

// Configuration for auto-prompt suggestions
const AUTO_PROMPTS = [
  {
    id: 'va-decision',
    title: 'VA Decision',
    displayText: 'Analyzing VA Decision...',
    prompt: `Please analyze my VA decision and provide:

### Service Connection + Special Monthly Compensation
- What conditions are service-connected?
- Am I eligible for any Special Monthly Compensation?
- What potential increases exist for my SC conditions?

### Denied Claims
- What favorable findings were identified?
- What were the specific reasons for denial?

### Effective Dates
- What is my overall effective date?
- What are the individual effective dates for each condition?

### Outcomes & Metrics
- What is my overall combined rating percentage?
- What are the individual percentages per condition?
- What is my monthly payment amount?
- How does my dependent info (married, children) affect my benefits?`,
  },
  {
    id: 'dd214',
    title: 'Summarize DD214',
    displayText: 'Analyzing DD214 Service Records...',
    prompt: `Please analyze my DD214 and service records to provide:

### Service Information
- What are all the details of my military service?
- Service dates and branches
- Units and assignments

### Locations (Presumptive Conditions)
- What locations did I serve in?
- Are any of these locations eligible for presumptive conditions?
- Any exposure to hostile zones (for MH fear of hostiles)?

### Awards and Decorations
- Do I have CIB, CAR, or Purple Heart?
- What other awards and decorations did I receive?
- How do these support my claims?

### MOS (Military Occupational Specialty)
- What was my MOS?
- What duties and hazards were associated with it?

### Character of Discharge
- What is my discharge status?
- How does this affect my benefits eligibility?`,
  },
  {
    id: 'current-conditions',
    title: 'Current Conditions',
    displayText: 'Analyzing current medical conditions...',
    prompt: `Please analyze my current medical conditions and provide:\n\n### Sources\n- Review records from VA Blue Button\n- Review records from Private/VA Providers\n- Identify which sources contain information about each condition\n\n### Current Diagnosis (Dx)\n- What are all my diagnosed conditions?\n- When was each condition diagnosed?\n- Who diagnosed each condition?\n\n### Current Severity\n- What is the current severity level of each condition?\n- How has severity changed over time?\n- What functional limitations are documented?\n\n### Lab Results\n- What relevant lab results support my conditions?\n- Are there any abnormal findings?\n- How do lab results correlate with my diagnoses?\n\n### Chronicity\n- How long have I had each condition?\n- Is there evidence of continuous treatment?\n- Are my conditions chronic or permanent?`,
  },
  {
    id: 'service-treatment-records',
    title: 'Service Treatment Records (STRs)',
    displayText: 'Analyzing Service Treatment Records...',
    prompt: `Please analyze my Service Treatment Records (STRs) and provide information about all conditions documented during service:

### All Conditions Found in STRs
For each condition documented (including those not currently service-connected), please provide:

### Date and Timeline
- When was each condition first documented?
- What is the chronology of treatment?
- Are there recurring patterns or complaints?

### Location/Place
- Where was treatment provided (base clinic, field hospital, etc.)?
- Which military treatment facilities were involved?
- Were there any deployments or duty stations associated with treatment?

### Diagnosis Details
- What specific diagnoses were given?
- Were there differential diagnoses considered?
- How did diagnoses evolve over time?

### Severity Documentation
- What severity levels were documented?
- Were there duty limitations or profiles issued?
- Was there lost duty time?

### SOAP Notes Review
- Subjective: What symptoms did I report?
- Objective: What did providers observe/measure?
- Assessment: What were the clinical assessments?
- Plan: What treatments were prescribed?

### Documentation References
- Which specific pages/documents contain key information?
- Are there any imaging or lab results referenced?
- What follow-up care was recommended?`,
  },

  {
    id: 'personal-statements',
    title: 'Personal Statement',
    displayText: 'Analyzing personal statements and documentation...',
    prompt: `Please analyze my personal statements and related documentation:

### All Transcripts Review
- What transcripts are available and linked to my conditions/events?
- What key statements support my claims?
- Are there consistent themes across my statements?

### Questionnaires Analysis
- Which questionnaires have been completed?
- Are all sections properly filled in and updated?
- Do the responses align with my claimed conditions?

### Exams & Medical Opinions (MOs)
- What C&P exams have been conducted?
- What medical opinions have been provided?
- Do the medical opinions support my claims?

### Service Treatment Records (STRs) Correlation
- How do my statements align with STR documentation?
- Are there supporting entries in my STRs?
- Is there a clear connection between service and current conditions?

### Discrepancy Analysis
- Are there any discrepancies between records, exams, and statements?
- What inconsistencies need to be addressed?
- How can these discrepancies be reconciled?

### Theory of Entitlement (ToE) Alignment
- Does my statement support a clear Theory of Entitlement?
- Is the ToE properly derived from medical opinions?
- Are all elements of service connection addressed?

### Chronicity & Severity Documentation
- Is chronicity (continuous symptoms since service) documented?
- Is current severity fully described in personal statements?
- Are functional impacts and daily life limitations included?
- What additional details should be added to strengthen the statement?`,
  },
];

// Generate signed URLs for documents that need them
const generateSignedUrlsForDocuments = async (documents) => {
  if (!documents?.length) return documents;

  const documentsNeedingUrls = documents.filter(
    (doc) => !doc.imageUrl && doc.s3Path,
  );
  if (!documentsNeedingUrls.length) return documents;

  // Create array with both image and PDF paths: [img1, pdf1, img2, pdf2, ...]
  const s3Paths = documentsNeedingUrls.flatMap((doc) =>
    [
      doc.s3Path, // image path
      doc.pdfUrl, // PDF path
    ].filter(Boolean),
  );

  if (!s3Paths.length) return documents;

  try {
    const { data } = await axios.post('/claims/files/signed-url-for-path', {
      s3Paths,
    });
    const urlMap = {};
    data?.forEach((result) => {
      if (result.url) urlMap[result.key] = result.url;
    });

    return documents.map((doc) => ({
      ...doc,
      id: doc.id || `doc-${Math.random().toString(36).substring(2, 11)}`,
      imageUrl: doc.imageUrl || urlMap[doc.s3Path] || null,
      pdfUrl: urlMap[doc.pdfUrl] || doc.pdfUrl || null,
    }));
  } catch (error) {
    console.error('Error generating signed URLs:', error);
    return documents.map((doc) => ({
      ...doc,
      id: doc.id || `doc-${Math.random().toString(36).substring(2, 11)}`,
    }));
  }
};

function EmptyState({ onSuggestionClick }) {
  // Use the first 5 prompts for the initial display
  // Can easily change which prompts to show or add a 'show more' button
  const displayPrompts = AUTO_PROMPTS.slice(0, 5);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        minHeight: '400px',
        padding: '20px',
        textAlign: 'center',
      }}
    >
      <div style={{ marginBottom: '24px' }}>
        <h2
          style={{
            fontSize: '28px',
            fontWeight: '600',
            color: '#111827',
            marginBottom: '8px',
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          }}
        >
          CaseGPT
        </h2>
        <p
          style={{
            fontSize: '16px',
            color: '#6b7280',
            margin: '0',
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          }}
        >
          Ask questions about claim details, uploaded documents, and call
          transcripts
        </p>
      </div>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          justifyContent: 'center',
          maxWidth: '900px',
          width: '100%',
        }}
      >
        {displayPrompts.map((suggestion) => (
          <button
            key={suggestion.id}
            onClick={() => onSuggestionClick(suggestion)}
            style={{
              padding: '10px 16px',
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              fontSize: '13px',
              fontWeight: '500',
              color: '#111827',
              fontFamily:
                '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f9fafb';
              e.currentTarget.style.borderColor = '#d1d5db';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#ffffff';
              e.currentTarget.style.borderColor = '#e5e7eb';
            }}
          >
            {suggestion.title}
          </button>
        ))}
      </div>
    </div>
  );
}

function ChatHistoryControls({
  selectedChatId,
  chatsData,
  isLoadingChats,
  isLoadingMessages,
  deleteChatMutation,
  handleChatChange,
  handleDeleteChat,
}) {
  return (
    <div
      className="chat-history-controls mb-2"
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: '12px',
        padding: '10px 0',
      }}
    >
      <span style={{ fontSize: '14px', color: '#6b7280' }}>Chat History</span>
      <Form.Select
        value={selectedChatId}
        onChange={handleChatChange}
        disabled={isLoadingChats}
        size="sm"
        style={{ width: '200px' }}
      >
        <option value="new">New Chat</option>
        {chatsData?.chats?.map((chat) => (
          <option key={chat.chatId} value={chat.chatId}>
            {chat.chatTitle.length > 25
              ? `${chat.chatTitle.substring(0, 25)}...`
              : chat.chatTitle}
          </option>
        ))}
      </Form.Select>

      {selectedChatId !== 'new' && (
        <Button
          variant="outline-danger"
          size="sm"
          onClick={handleDeleteChat}
          disabled={deleteChatMutation.isLoading}
        >
          {deleteChatMutation.isLoading ? (
            <Spinner
              as="span"
              animation="border"
              size="sm"
              role="status"
              aria-hidden="true"
            />
          ) : (
            'Delete Chat'
          )}
        </Button>
      )}
    </div>
  );
}

export function CaseGPTChat({ data }) {
  const userId = data.user.userId;
  const claimId = data.claimId || data.claim.claimId;

  const [selectedChatId, setSelectedChatId] = useState('new');
  const [currentChatId, setCurrentChatId] = useState(null);
  const [messagesData, setMessagesData] = useState(null);
  const [chatsData, setChatsData] = useState(null);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isLoadingChats, setIsLoadingChats] = useState(false);

  const url = `${process.env.REACT_APP_API_URL}/casegpt/chat`;

  // Fetch chats when claimId changes
  useEffect(() => {
    const loadChats = async () => {
      setIsLoadingChats(true);
      try {
        const data = await fetchChatsByClaim(claimId);
        setChatsData(data);
      } catch (error) {
        console.error('Failed to fetch chats:', error);
        setChatsData({ chats: [] });
      } finally {
        setIsLoadingChats(false);
      }
    };

    loadChats();
  }, [claimId]);

  // Fetch messages when selectedChatId changes
  useEffect(() => {
    const loadMessages = async () => {
      if (!selectedChatId || selectedChatId === 'new') {
        setMessagesData({ messages: [] });
        setIsLoadingMessages(false);
        return;
      }

      setIsLoadingMessages(true);
      // Clear current messages immediately to show loading state
      setMessages([]);

      try {
        const data = await fetchChatMessages(selectedChatId);
        setMessagesData(data);
      } catch (error) {
        console.error('Failed to fetch messages:', error);
        setMessagesData({ messages: [] });
      } finally {
        setIsLoadingMessages(false);
      }
    };

    loadMessages();
  }, [selectedChatId]);

  const deleteChatMutation = useDeleteChat(
    () => {
      setSelectedChatId(null);
      setCurrentChatId(null);
      refetchChats();
      toast.success('Chat deleted successfully');
    },
    {
      onError: (error) => {
        console.error('Failed to delete chat:', error);
        toast.error('Failed to delete chat');
      },
    },
  );

  // Handle chat selection
  const handleChatChange = (e) => {
    const chatId = e.target.value;
    setSelectedChatId(chatId);
    setCurrentChatId(chatId);
  };

  // Handle delete chat
  const handleDeleteChat = () => {
    if (
      selectedChatId !== 'new' &&
      window.confirm('Are you sure you want to delete this chat?')
    ) {
      deleteChatMutation.mutate(selectedChatId);
    }
  };

  // Custom chat state
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoadingResponse, setIsLoadingResponse] = useState(false);
  const [isAutoPrompt, setIsAutoPrompt] = useState(false);
  const [showFileSelector, setShowFileSelector] = useState(false);
  const [selectedFileIds, setSelectedFileIds] = useState([]);
  const [claimFiles, setClaimFiles] = useState([]);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  // Remove auto-scroll - let users control scrolling

  // Auto-resize textarea based on content
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = '54px';
      const scrollHeight = textareaRef.current.scrollHeight;
      textareaRef.current.style.height = `${Math.min(scrollHeight, 200)}px`;
    }
  }, [input]);

  // Load claim files for file selector
  useEffect(() => {
    const loadClaimFiles = async () => {
      try {
        const response = await axios.get(`/claims/${claimId}/files`);
        if (response.data && Array.isArray(response.data)) {
          const activeFiles = response.data.filter((f) => f.fileActive);
          setClaimFiles(activeFiles);
          // Initialize with no files selected (user must select files)
          setSelectedFileIds([]);
        }
      } catch (error) {
        console.error('Failed to load claim files:', error);
        setClaimFiles([]);
      }
    };

    if (claimId) {
      loadClaimFiles();
    }
  }, [claimId]);

  // Load messages when messagesData changes
  useEffect(() => {
    const loadMessagesWithSignedUrls = async () => {
      if (messagesData?.messages) {
        const formattedMessages = [
          {
            role: 'assistant',
            content: 'Hi there! I am the CaseGPT. How can I help you today?',
          },
          ...(await Promise.all(
            messagesData.messages.map(async (msg) => {
              // Parse documents from content if they exist
              let content = msg.content;
              let documents = null;

              // Check if content contains document JSON at the end
              // Look for actual JSON array structure: [{
              const docMatch = content.match(/(\s*\[\s*\{[\s\S]*\]\s*)$/);
              if (docMatch) {
                try {
                  const parsedDocuments = JSON.parse(docMatch[1].trim());
                  // Generate signed URLs for documents that need them
                  documents =
                    await generateSignedUrlsForDocuments(parsedDocuments);
                  // Remove the documents JSON from the visible content
                  content = content.replace(/(\s*\[\s*\{[\s\S]*\]\s*)$/, '');
                } catch (e) {
                  console.warn(
                    'Failed to parse documents from message content:',
                    e,
                  );
                }
              }

              return {
                role: msg.role,
                content,
                documents,
              };
            }),
          )),
        ];
        setMessages(formattedMessages);
      } else {
        setMessages([
          {
            role: 'assistant',
            content: 'Hi there! I am the CaseGPT. How can I help you today?',
          },
        ]);
      }
    };

    loadMessagesWithSignedUrls();
  }, [messagesData, claimId]);

  // Handle Enter key for submission (Shift+Enter for newline)
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // Handle message submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoadingResponse) return;

    const userMessage = {
      role: 'user',
      content: input,
      isAutoPrompt: typeof isAutoPrompt === 'string' ? true : isAutoPrompt,
      displayText: typeof isAutoPrompt === 'string' ? isAutoPrompt : null,
    };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = '54px';
    }
    setIsLoadingResponse(true);
    setIsAutoPrompt(false);

    try {
      // Create chat if needed
      let chatId = currentChatId;
      if (!chatId || chatId === 'new') {
        const chatResponse = await axios.post('/casegpt/chats', {
          claimId,
          userId,
          chatTitle: input.substring(0, 50),
        });
        chatId = chatResponse.data.chatId;
        setCurrentChatId(chatId);
      }

      // Send message to streaming endpoint
      const token = getCookie('token');
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          claimId,
          userId,
          chatId,
          selectedFileIds,
          messages: messages
            .filter(
              (m) =>
                !(m.role === 'assistant' && m.content.includes('Hi there')),
            )
            .concat(userMessage)
            .map((m) => ({ role: m.role, text: m.content })),
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const reader = response.body.getReader();
      let assistantMessage = '';
      let documents = null;

      // Add placeholder for assistant message
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: '', documents: null },
      ]);

      // Read the streaming response
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = new TextDecoder().decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6); // Remove 'data: ' prefix

            // Skip [DONE] messages
            if (data === '[DONE]') continue;

            let contentToAdd = '';

            // Handle structured JSON responses
            try {
              const parsed = JSON.parse(data);

              // Check if this is the final response with documents
              if (parsed.isEndOfAiResponseStream && parsed.ocrPagePreviews) {
                documents = parsed.ocrPagePreviews;
              } else if (parsed.text !== undefined) {
                contentToAdd = parsed.text;
              } else {
                contentToAdd = data;
              }
            } catch (e) {
              // Not structured JSON, just use as text
              contentToAdd = data;
            }

            // Only add text content (not JSON arrays)
            if (contentToAdd && !contentToAdd.trim().startsWith('[')) {
              assistantMessage += contentToAdd;
            }

            // Update the message with current content and documents
            const currentMessage = assistantMessage;
            const currentDocuments = documents;
            setMessages((prev) => {
              const newMessages = [...prev];
              newMessages[newMessages.length - 1] = {
                role: 'assistant',
                content: currentMessage,
                documents: currentDocuments,
              };
              return newMessages;
            });
          }
        }
      }

      // Refresh chat history after successful response
      setTimeout(async () => {
        await refetchChats();
        if (chatId !== currentChatId) {
          setSelectedChatId(chatId);
        }
      }, 1000);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: 'Sorry, I encountered an error. Please try again.',
        },
      ]);
    } finally {
      setIsLoadingResponse(false);
    }
  };

  // Refetch chats function
  const refetchChats = async () => {
    if (claimId) {
      setIsLoadingChats(true);
      try {
        const data = await fetchChatsByClaim(claimId);
        setChatsData(data);
      } catch (error) {
        console.error('Failed to refetch chats:', error);
      } finally {
        setIsLoadingChats(false);
      }
    }
  };

  return (
    <div className="casegpt-chat-container">
      {/* Chat History Controls - Top */}
      <ChatHistoryControls
        selectedChatId={selectedChatId}
        setSelectedChatId={setSelectedChatId}
        chatsData={chatsData}
        isLoadingChats={isLoadingChats}
        isLoadingMessages={isLoadingMessages}
        deleteChatMutation={deleteChatMutation}
        handleChatChange={handleChatChange}
        handleDeleteChat={handleDeleteChat}
      />

      {/* ChatGPT-style Chat Interface */}
      <div
        className="chatgpt-chat-container"
        style={{
          height: 'calc(100vh - 260px)',
          minHeight: '500px',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#ffffff',
        }}
      >
        {/* Chat Messages Area */}
        <div
          className="flex-fill"
          style={{
            overflowY: 'auto',
            padding: '20px 0 0 0',
            backgroundColor: '#ffffff',
          }}
        >
          <div
            className="chat-messages"
            style={{
              maxWidth: '750px',
              margin: '0 auto',
              padding: '0 20px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {isLoadingMessages ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  flexDirection: 'column',
                  color: '#6b7280',
                }}
              >
                <Spinner animation="border" style={{ marginBottom: '12px' }} />
                <span style={{ fontSize: '16px' }}>
                  Loading chat messages...
                </span>
              </div>
            ) : messages.length <= 1 && !isLoadingResponse ? (
              <EmptyState
                onSuggestionClick={(suggestion) => {
                  setInput(suggestion.prompt);
                  setIsAutoPrompt(suggestion.displayText || true);
                  // Auto-submit the form
                  setTimeout(() => {
                    const form = document.querySelector(
                      '.casegpt-chat-container form',
                    );
                    if (form) {
                      form.dispatchEvent(
                        new Event('submit', {
                          cancelable: true,
                          bubbles: true,
                        }),
                      );
                    }
                  }, 100);
                }}
              />
            ) : (
              messages.map((message, index) => (
                <div
                  key={index}
                  className="mb-4"
                  style={{
                    display: 'flex',
                    justifyContent:
                      message.role === 'user' ? 'flex-end' : 'flex-start',
                  }}
                >
                  <div
                    style={{
                      backgroundColor:
                        message.role === 'user' ? '#f7f7f8' : '#ffffff',
                      padding: '16px 20px',
                      borderRadius: '8px',
                      border: 'none',
                      maxWidth: message.role === 'user' ? '70%' : '100%',
                    }}
                  >
                    <div
                      style={{
                        fontSize: '16px',
                        lineHeight: '1.6',
                        color: '#374151',
                        fontFamily:
                          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      }}
                    >
                      {message.role === 'user' ? (
                        message.isAutoPrompt ? (
                          <div
                            style={{ fontStyle: 'italic', color: '#6b7280' }}
                          >
                            {message.displayText || 'Processing request...'}
                          </div>
                        ) : (
                          <div style={{ whiteSpace: 'pre-wrap' }}>
                            {message.content}
                          </div>
                        )
                      ) : (
                        <>
                          <div
                            style={{
                              marginBottom: message.content.includes(
                                '**Confidence:',
                              )
                                ? '12px'
                                : '0',
                            }}
                          >
                            <ReactMarkdown>
                              {message.content.replace(
                                /\*\*Confidence:\s*\d+%\*\*/g,
                                '',
                              )}
                            </ReactMarkdown>
                          </div>
                          {message.content.includes('**Confidence:') && (
                            <div style={{ marginBottom: '12px' }}>
                              <span
                                style={{
                                  display: 'inline-block',
                                  backgroundColor: '#f3f4f6',
                                  color: '#6b7280',
                                  fontSize: '12px',
                                  padding: '4px 8px',
                                  borderRadius: '12px',
                                  fontWeight: '500',
                                }}
                              >
                                {message.content
                                  .match(/\*\*Confidence:\s*\d+%\*\*/)?.[0]
                                  ?.replace(/\*\*/g, '') || ''}
                              </span>
                            </div>
                          )}
                          {message.documents &&
                            message.documents.length > 0 && (
                              <DocumentGrid documents={message.documents} />
                            )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}

            {isLoadingResponse && (
              <div
                className="mb-4"
                style={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                }}
              >
                <div
                  style={{
                    backgroundColor: '#ffffff',
                    padding: '16px 20px',
                    borderRadius: '8px',
                    border: 'none',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Spinner animation="border" size="sm" className="me-2" />
                    <span
                      style={{
                        fontSize: '16px',
                        color: '#6b7280',
                        fontFamily:
                          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      }}
                    >
                      Thinking...
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* ChatGPT-style Input Area */}
        <div
          style={{
            padding: '0 30px 30px 30px',
            backgroundColor: '#ffffff',
            flexShrink: 0,
          }}
        >
          <div
            style={{ maxWidth: '750px', margin: '0 auto', padding: '0 20px' }}
          >
            <Form onSubmit={handleSubmit} style={{ position: 'relative' }}>
              <Form.Control
                as="textarea"
                ref={textareaRef}
                rows={1}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask me anything about your case..."
                disabled={isLoadingResponse}
                style={{
                  borderRadius: '24px',
                  border: '1px solid #d1d5db',
                  padding: '16px 110px 16px 20px',
                  fontSize: '18px',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
                  backgroundColor: 'white',
                  fontFamily:
                    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  outline: 'none',
                  resize: 'none',
                  minHeight: '54px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  right: '54px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                }}
              >
                <Button
                  type="button"
                  onClick={() => setShowFileSelector(true)}
                  disabled={isLoadingResponse || claimFiles.length === 0}
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    border: 'none',
                    backgroundColor: 'transparent',
                    color: claimFiles.length > 0 ? '#6b7280' : '#d1d5db',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0',
                    fontSize: '16px',
                    transition: 'all 0.2s ease',
                    cursor: claimFiles.length > 0 ? 'pointer' : 'default',
                    position: 'relative',
                  }}
                  title={
                    claimFiles.length > 0
                      ? 'Select files for context'
                      : 'No files available'
                  }
                >
                  <FontAwesomeIcon icon={faPaperclip} />
                  {selectedFileIds.length > 0 && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '-3px',
                        right: '-3px',
                        backgroundColor: '#0066cc',
                        color: 'white',
                        borderRadius: '40%',
                        width: '18px',
                        height: '18px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '10px',
                        fontWeight: 'bold',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      }}
                    >
                      {selectedFileIds.length}
                    </div>
                  )}
                </Button>
              </div>
              <Button
                type="submit"
                disabled={isLoadingResponse || !input.trim()}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  border: 'none',
                  backgroundColor: !input.trim() ? '#d1d5db' : '#0066cc',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0',
                  fontSize: '16px',
                  transition: 'all 0.2s ease',
                }}
              >
                {isLoadingResponse ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                  </svg>
                )}
              </Button>
            </Form>
          </div>
        </div>

        {/* File Selector Modal */}
        <FileSelector
          show={showFileSelector}
          onHide={() => setShowFileSelector(false)}
          claimFiles={claimFiles}
          selectedFileIds={selectedFileIds}
          onSelectedFilesChange={setSelectedFileIds}
          user={data.user}
        />
      </div>
    </div>
  );
}

export default CaseGPTChat;
